<template>
  <div v-if="provider">
    <v-card
      class="pa-0 border-t-0"
      border
      tile
    >
      <v-card-text>
        <v-row class="d-flex align-start">
          <v-col>
            <div class="c-black">
              <v-row
                class="ma-0 pa-0 d-flex align-center"
                dense
              >
                <v-col cols="11">
                  <h4
                    class="c-black fw-600 fs-20"
                    data-cy="selection-name"
                    tabindex="0"
                    dense
                  >
                    {{ provider.name }}
                  </h4>
                  <div
                    v-if="provider.license_business_name != provider.name"
                    class="fs-16"
                  >
                    {{ provider.license_business_name }}
                  </div>
                </v-col>
              </v-row>

              <v-row
                class="ma-0 pa-0"
                dense
              >
                <v-col>
                  <span
                    class="fs-16 fw-400 me-1"
                    tabindex="0"
                  >
                    {{ providerAddress }}
                  </span>

                  <span
                    v-if="provider.distance_from_origin"
                    class="fs-16 fw-400"
                  >
                    - {{ provider.distance_from_origin }} {{ $bct('miles away') }}
                  </span>
                </v-col>
              </v-row>
            </div>
          </v-col>
        </v-row>

        <template v-if="provider.public_notice">
          <PublicNotice
            v-if="provider.public_notice"
            :public-notice="provider.public_notice"
            small
          />
        </template>
      </v-card-text>
    </v-card>

    <v-card
      v-for="program in provider.programs"
      :key="program.id"
      class="px-3 py-0 border-t-0"
      border
      tile
    >
      <PublicNotice
        v-if="program.public_notice"
        :public-notice="program.public_notice"
        small
      />
      <v-row
        class="d-flex align-center"
        dense
      >
        <v-col
          class="py-6"
          cols="1"
        >
          <template v-if="frozenProgramIds.includes(program.id)">
            <v-checkbox
              :aria-label="'Selected ' + program.name"
              :model-value="true"
              class="py-0 my-0"
              density="compact"
              disabled
              hide-details
            />
          </template>
          <template v-else>
            <v-checkbox
              @click="$emit('check', [program.id, $event])"
              @update:model-value="$emit('toggle', [program.id, $event])"
              :aria-label="getAriaLabel(program)"
              :data-cy="`program-checkbox-${program.id}`"
              :indeterminate="
                isIneligible(program) || (!programIds.includes(program.id) && !selectable)
              "
              :model-value="programIds.includes(program.id)"
              :readonly="(!programIds.includes(program.id) && !selectable) || isIneligible(program)"
              class="py-0 my-0"
              density="compact"
              hide-details
            />
          </template>
        </v-col>

        <v-col class="ml-4 ml-md-0">
          {{ $bct(program.name) }}
        </v-col>

        <v-col
          v-if="isIneligible(program)"
          class="d-flex justify-end"
        >
          <v-tooltip
            location="bottom"
            max-width="600"
          >
            <template #activator="{ props: tooltipProps }">
              <v-chip
                v-bind="tooltipProps"
                class="ma-2"
              >
                {{ $bct('Requirements not met') }}
              </v-chip>
            </template>

            <ul>
              <li
                v-for="(message, index) in getIneligibilityMessage(program)"
                :key="index"
              >
                {{ $bct(message) }}
              </li>
            </ul>
          </v-tooltip>
        </v-col>

        <v-col
          v-if="!isIneligible(program) && showAvailability(program)"
          class="d-flex justify-end"
          data-testid="seat-availability-badge"
        >
          <SeatsAvailability :program="program" />
        </v-col>
      </v-row>
    </v-card>

    <v-card
      class="border-t-0"
      border
      tile
    >
      <template v-if="!hideViewMore">
        <v-card-actions class="bt-1 border-t-0 px-5 bc-extra-light-grey pointer justify-end">
          <v-btn
            @click="$emit('open')"
            append-icon="chevron_right"
            class="focus-very-visible"
            color="primary"
            data-testid="provider-programs-card-view-more"
            variant="outlined"
          >
            {{ $bct('View more information') }}
          </v-btn>
        </v-card-actions>
      </template>
    </v-card>
  </div>
</template>

<script setup>
import { formatAddress } from '@/shared/services/address';
import PublicNotice from '@/shared/components/PublicNotice.vue';
import SeatsAvailability from '@/shared/components/SeatsAvailability.vue';

const props = defineProps({
  frozenProgramIds: {
    default: () => [],
    type: Array,
  },
  hideToggle: Boolean,
  hideViewMore: Boolean,
  dob: {
    default: null,
    type: String,
  },
  provider: {
    default: null,
    type: Object,
  },
  programIds: {
    default: null,
    type: Array,
  },
  selectable: {
    default: true,
    type: Boolean,
  },
  subsidyProgram: {
    default: null,
    type: Object,
  },
});

defineEmits(['check', 'open', 'toggle']);

const $bct = inject('translator');

const enableSeatAvailability = computed(
  () => props.subsidyProgram?.enable_show_seat_availability === true,
);

const providerAddress = computed(() => {
  if (!props.provider) return null;

  return formatAddress(props.provider.address1, props.provider.city, props.provider.zip);
});

function availabilityLabel(program) {
  switch (program?.meta?.seat_availability_flag) {
    case 'available':
      return 'Seats available';
    case 'low':
      return 'Seats available - Low availability!';
    case 'none':
      return 'No seats available';
    default:
      return null;
  }
}

function showAvailability(program) {
  return enableSeatAvailability.value && availabilityLabel(program) !== null;
}

function dobIneligible(program) {
  return (props.dob && props.dob < program?.dob_start_date) || props.dob > program?.dob_end_date;
}

function hasIneligibleProviderProgramRequirements(program) {
  return (
    program?.provider_program_requirements_id &&
    program?.provider_program_requirements?.some((ppr) => !ppr.passes)
  );
}

function isIneligible(program) {
  return dobIneligible(program) || hasIneligibleProviderProgramRequirements(program);
}

function getAriaLabel(program) {
  return `${$bct('Select')} ${$bct(program.name)}`;
}

function getIneligibilityMessage(program) {
  const messages = [];

  if (dobIneligible(program)) {
    messages.push(
      `${$bct("The child's date of birth must fall within the following dates")} ${program?.dob_start_date}, ${program?.dob_end_date}`,
    );
  }

  if (hasIneligibleProviderProgramRequirements(program)) {
    program.provider_program_requirements
      .filter((ppr) => !ppr.passes)
      .forEach((ppr) => messages.push($bct(ppr.message)));
  }

  return messages;
}
</script>
